import type {
  TournamentPublicRegistrationPrice,
  TournamentRegistrationQuote,
} from "@bw/shared/types/generated";
import { parseDecimal } from "@bw/shared/utils";

export type TournamentEntryFeeState =
  | { kind: "free" }
  | { kind: "paid" }
  | { kind: "player_specific" }
  | { kind: "unavailable" };

function hasValidCurrency(value: unknown): value is string {
  return typeof value === "string" && /^[A-Za-z]{3}$/.test(value);
}

/**
 * Interpret the backend's player-specific registration quote without
 * recomputing money in the browser. Missing, malformed, negative, or internally
 * inconsistent quotes fail closed.
 */
export function getTournamentRegistrationQuoteState(
  quote: TournamentRegistrationQuote | null | undefined,
): TournamentEntryFeeState {
  if (quote == null) return { kind: "unavailable" };

  const amount = parseDecimal(quote.amount);
  if (amount === null || amount < 0 || !hasValidCurrency(quote.currency)) {
    return { kind: "unavailable" };
  }
  if (amount === 0 && quote.requires_payment === false) {
    return { kind: "free" };
  }
  if (amount > 0 && quote.requires_payment === true) {
    return { kind: "paid" };
  }
  return { kind: "unavailable" };
}

/** Interpret the backend-owned anonymous catalog pricing state. */
export function getTournamentPublicRegistrationPriceState(
  price: TournamentPublicRegistrationPrice | null | undefined,
): TournamentEntryFeeState {
  if (price == null) return { kind: "unavailable" };
  const amount = parseDecimal(price.amount);
  const validCurrency = hasValidCurrency(price.currency);

  if (price.kind === "free" && amount === 0 && validCurrency) {
    return { kind: "free" };
  }
  if (
    price.kind === "fixed" &&
    amount !== null &&
    amount > 0 &&
    validCurrency
  ) {
    return { kind: "paid" };
  }
  if (
    price.kind === "player_specific" &&
    price.amount == null &&
    validCurrency
  ) {
    return { kind: "player_specific" };
  }
  return { kind: "unavailable" };
}
